import { SectionWithContent } from '@core';
import { useQueryParamsClear } from '@modules';
import { Box, Flex, Grid } from '@themed';
import React from 'react';
import { ComparisonHeader } from '../../finnoscore-comparison/ComparisonSelector';
import { InstitutionDropDownWithState } from '../../finnoscore-comparison/InstitutionGroupedDropDown';
import { ChartItem, ExportButton } from '../shared/components/base';
import { ChartDropDownHeader } from '../shared/components/base/ChartDropDownControl';
import { ChartWizardStep } from '../shared/components/base/ChartWizardStep';
import { finnoChartStyles, finnoChartStyles as gridStyles } from '../shared/components/base/index.styles';
import { ChartDropDownControlComponent, CheckboxDropDownControlComponent } from '../shared/components/control/ChartDropDownControl';
import { DefaultChartData } from '../shared/helpers';
import useDimensionDropDownControl from '../shared/hooks/useDimensionDropDownControl';
import { useInstitutionDropDownControl } from '../shared/hooks/useInstitutionDropDownControl';
import useMultiChartInstitutions from '../shared/hooks/useMultiChartInstitutions';
import { usePeriodDropDownControl } from '../shared/hooks/usePeriodDropDownControl';
import { useTypeDropDownControl } from '../shared/hooks/useTypeDropDownControl';
import { chartStyles } from '../shared/styles';
import { MultiComparisonChart } from './components';
import { ColorPaletteList } from './components/ColorPaletteList';

const contentStyles = {
  ...gridStyles.content,
  gridTemplateRows: ['max-content max-content 1fr 1fr auto auto'],
};

export const MultiComparison = () => {
  const exportChartRef = React.useRef<HTMLDivElement>(null);
  const exportLegendRef = React.useRef<HTMLDivElement>(null);

  const { clearQueryParams } = useQueryParamsClear();

  const { types, setTypeId, typeId, type: fsType, isLoading: isTypeLoading } = useTypeDropDownControl();
  const {
    periods: pastPeriodList,
    period: pastPeriod,
    periodId: pastPeriodId,
    setPeriodId: setFirstPeriodId,
    isLoading: isFirstPeriodLoading,
  } = usePeriodDropDownControl({ type: fsType, paramKey: 'relativePeriodId' });

  const {
    periods: currentPeriodList,
    period: currentPeriod,
    periodId: currentPeriodId,
    setPeriodId: setcurrentPeriodId,
  } = usePeriodDropDownControl({ type: fsType, paramKey: 'currentPeriodId' });

  const {
    institutions,
    institution,
    institutionSetHandler,
    isLoading: isInstitutionLoading,
  } = useInstitutionDropDownControl({
    type: fsType,
    period: currentPeriod,
    paramKey: 'institutionId',
  });

  const {
    relative,
    current,
    national,
    worldwide,
    isLoading: isLegendLoading,
  } = useMultiChartInstitutions(fsType?.text, pastPeriod, currentPeriod, institution?.name, institution?.country?.countryCode);

  const { dimensionKeys, onCheck } = useDimensionDropDownControl(institution);

  const handleTypeChange = (selectedTypeId: string) => {
    if (typeId !== selectedTypeId) {
      clearQueryParams();
    }
    setTypeId(selectedTypeId);
  };

  return (
    <SectionWithContent sx={finnoChartStyles.container}>
      <Grid sx={contentStyles}>
        <ChartWizardStep id='type' label='Type' isLoading={isTypeLoading}>
          <ChartDropDownControlComponent zIndex={20} list={types} selectedId={typeId} setter={handleTypeChange}>
            <ChartDropDownHeader title={fsType?.text || 'Choose a type'} />
          </ChartDropDownControlComponent>
        </ChartWizardStep>

        {fsType && (
          <ChartWizardStep id='pastPeriod' label='Relative period' isLoading={isFirstPeriodLoading}>
            <ChartDropDownControlComponent zIndex={15} list={pastPeriodList} selectedId={pastPeriodId} setter={setFirstPeriodId}>
              <ChartDropDownHeader title={pastPeriod?.text || 'Choose a period'} />
            </ChartDropDownControlComponent>
          </ChartWizardStep>
        )}

        {fsType && (
          <ChartWizardStep id='currentPeriod' label='Current period' isLoading={isFirstPeriodLoading} loading={null}>
            <ChartDropDownControlComponent zIndex={10} list={currentPeriodList} selectedId={currentPeriodId} setter={setcurrentPeriodId}>
              <ChartDropDownHeader title={currentPeriod?.text || 'Choose a period'} />
            </ChartDropDownControlComponent>
          </ChartWizardStep>
        )}

        {fsType && currentPeriod && (
          <ChartWizardStep id='bankSelector' label='Institution' isLoading={isInstitutionLoading}>
            <InstitutionDropDownWithState
              institutions={institutions}
              onSelect={institutionSetHandler}
              selectedInstitution={institution}
              sx={{ zIndex: 15 }}
              header={
                <ComparisonHeader
                  subject={institution}
                  chartComponent={<ChartItem percentage={(institution?.total || 0) * 10} sx={chartStyles.headerChart} />}
                />
              }
            />

            <ChartWizardStep id='bankSelector' label='Dimensions' isLoading={!(fsType && institution)}>
              <CheckboxDropDownControlComponent zIndex={10} list={institution?.dimensions} selectedList={dimensionKeys} onCheckHandler={onCheck}>
                <ChartDropDownHeader title={`Selected dimensions (${dimensionKeys?.length}/6)`} />
              </CheckboxDropDownControlComponent>
            </ChartWizardStep>

            <ChartWizardStep id='bankSelector' label='Color palette' isLoading={isLegendLoading} ref={exportLegendRef}>
              <ColorPaletteList institutions={[current, relative, national, worldwide]} />
            </ChartWizardStep>
          </ChartWizardStep>
        )}

        {institution && (
          <Flex id='downloadButton' sx={{ height: 'min-content' }}>
            <ExportButton element={exportChartRef.current} type='chart' name={`multi.${institution.name}`} />
            <ExportButton element={exportLegendRef.current} type='legend' />
          </Flex>
        )}

        <Box id='chart' variant={institution ? undefined : 'styles.sampleChart'}>
          <MultiComparisonChart
            key={institution?.id}
            ref={exportChartRef}
            relative={relative || DefaultChartData.multi.relative}
            current={current || DefaultChartData.multi.current}
            national={national || DefaultChartData.multi.national}
            worldwide={worldwide || DefaultChartData.multi.worldwide}
            dimensions={dimensionKeys || undefined}
          />
        </Box>
      </Grid>
    </SectionWithContent>
  );
};
